import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { ShellRail } from '../../shell/AppShell';
import { managerIntelAPI, rateMyTradeAPI } from '../../utils/api';
import { ChevronRightIcon } from '../../shell/icons';
import { SleeperAvatar } from '../../shell/SleeperAvatar';

/**
 * IntelHome — the in-shell /intel landing. Search any Sleeper username,
 * scout your own league mates (active-league rosters), or jump back into
 * recently scouted profiles. The profile itself stays at
 * /manager-intel/:username (legacy surface until its dedicated rebuild).
 */

const IntelHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagues, sleeperUser } = useShell();
  // League-mates picker is local to this page — there is no global league.
  const [mateLeagueId, setMateLeagueId] = useState(null);
  const mateLeague = leagues.find((l) => l.leagueId === mateLeagueId) || leagues[0] || null;

  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [leagueMates, setLeagueMates] = useState(null);

  useEffect(() => {
    let active = true;
    managerIntelAPI
      .getRecentManagers(12)
      .then((res) => {
        if (!active) return;
        const list = res.data?.managers || res.data || [];
        if (Array.isArray(list)) setRecent(list);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user || !mateLeague) return undefined;
    let active = true;
    setLeagueMates(null);
    rateMyTradeAPI
      .getLeagueRosters(mateLeague.leagueId)
      .then((res) => { if (active) setLeagueMates(res.data?.teams || []); })
      .catch(() => { if (active) setLeagueMates([]); });
    return () => { active = false; };
  }, [user, mateLeague]);

  const scout = (e) => {
    if (e) e.preventDefault();
    const name = query.trim().replace(/^@/, '');
    if (name) navigate(`/manager-intel/${name.toLowerCase()}`);
  };

  return (
    <div className="ft-view">
      <Helmet>
        <title>Manager Intel — Fantasy Taco</title>
        <meta name="description" content="Scout any dynasty manager. Trade history, tendencies, championships — the full intel." />
      </Helmet>

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">Why scout first</span>
          <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
            Every manager has a pattern. Find it before you send the offer.
          </p>
        </div>
        {recent.length > 0 && (
          <div className="ft-railsec">
            <span className="ft-k">Recently scouted</span>
            {recent.slice(0, 6).map((m) => {
              const name = m.sleeperUsername || m.username;
              if (!name) return null;
              return (
                <button key={name} className="ft-railaction" onClick={() => navigate(`/manager-intel/${name}`)}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <SleeperAvatar url={m.avatarUrl} name={name} size={24} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  </span>
                  <ChevronRightIcon />
                </button>
              );
            })}
          </div>
        )}
      </ShellRail>

      <span className="ft-k brand">Manager Intel</span>
      <h1 className="ft-hero">Know who you trade with.</h1>
      <p className="ft-sub">Trade history, tendencies, titles — the full intel on any manager.</p>

      <form onSubmit={scout} style={{ display: 'flex', gap: 10, margin: '26px 0 10px', maxWidth: 520 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Any Sleeper username"
          style={{ flex: 1, background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 17px', fontSize: 15.5, outline: 'none' }}
        />
        <button type="submit" className="ft-btn">Scout</button>
      </form>

      {user && mateLeague && (
        <div className="ft-section">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <span className="ft-k">Scout {mateLeague.name}</span>
            {leagues.length > 1 && (
              <div className="ft-pillrow">
                {leagues.map((l) => (
                  <button
                    key={l.leagueId}
                    className={`ft-fpill${mateLeague.leagueId === l.leagueId ? ' on' : ''}`}
                    onClick={() => setMateLeagueId(l.leagueId)}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {leagueMates === null ? (
            <div className="ft-loading">Pulling the league…</div>
          ) : (
            <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
              {leagueMates
                .filter((t) => !sleeperUser?.userId || t.sleeperUserId !== sleeperUser.userId)
                .filter((t) => t.ownerName)
                .map((t) => (
                  <button key={t.rosterId} className="ft-leaguecard" onClick={() => navigate(`/manager-intel/${t.ownerName}`)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <SleeperAvatar id={t.avatar} name={t.ownerName} size={32} />
                      <div style={{ minWidth: 0 }}>
                        <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.ownerName}</div>
                        <div className="tm" style={{ marginBottom: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.teamName}</div>
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {recent.length > 0 && (
        <div className="ft-section">
          <span className="ft-k">Recently scouted</span>
          <div className="ft-pillrow" style={{ marginTop: 12 }}>
            {recent.map((m) => {
              const name = m.sleeperUsername || m.username;
              if (!name) return null;
              return (
                <button
                  key={name}
                  className="ft-fpill"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingLeft: 8 }}
                  onClick={() => navigate(`/manager-intel/${name}`)}
                >
                  <SleeperAvatar url={m.avatarUrl} name={name} size={22} />
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelHome;
